import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MessageSquare } from "lucide-react";
import { CATEGORIES, feet, timeAgo, fmtRemaining } from "./constants";
import GifImage from "../gifs/GifImage";
import TranslatedText from "../app/TranslatedText";

/**
 * Convert a hex color into an rgba(...) string with the given alpha. Used to
 * tint each vibe card's border/glow with its category color without blowing
 * out the dark palette.
 */
function hexToRgba(hex, alpha) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * VibeCard — one item in the LIVE FEED strip. Owns its own 10s "now" tick so
 * the countdown badge updates smoothly.
 *
 * Motion:
 *   • Each card is wrapped in a <motion.div layout> so when a NEW vibe pushes
 *     into the top of the feed, the existing cards physically slide DOWN into
 *     their new positions (conveyor-belt feel) via FLIP animation.
 *   • New cards enter with a soft slide-from-above + fade (see initial/animate
 *     in the parent AnimatePresence in LiveRoom.jsx).
 *
 * Style: the border + subtle outer glow are tinted to the category color so
 * you can read the mood at a glance without needing to check the label.
 */
export default function VibeCard({ v, blink }) {
  const { t } = useTranslation();
  const cat = CATEGORIES.find(c => c.id === v.category) || CATEGORIES[2];
  const ft = feet(v.distance_m);
  const ago = timeAgo(v.created_at);
  const isBiz = v.category === "LOCAL_BUSINESS";
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(t);
  }, []);
  let remaining = v.expires_in_seconds;
  if (v.expires_at) {
    remaining = Math.max(0, Math.floor((new Date(v.expires_at).getTime() - now) / 1000));
  }
  const expiringSoon = remaining != null && remaining < 300; // < 5 min

  const cardBorder = hexToRgba(cat.color, 0.55);
  const cardGlow = hexToRgba(cat.color, 0.18);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -36, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.98 }}
      transition={{
        type: "spring",
        stiffness: 380,
        damping: 32,
        mass: 0.9,
        opacity: { duration: 0.24 },
      }}
    >
      <Link
        to={`/vibe/${v.id}`}
        className="block rounded-3xl bg-black/60 p-4 transition relative"
        data-testid={`vibe-${v.id}`}
        style={{
          border: `1px solid ${cardBorder}`,
          boxShadow: `0 0 22px ${cardGlow}, inset 0 0 0 1px ${hexToRgba(cat.color, 0.08)}`,
        }}
      >
        <div className="flex items-center gap-2 text-[10px] font-mono-accent text-zinc-500">
          <span style={{ color: cat.color }}>●</span>
          <span style={{ color: cat.color }}>{cat.label.toUpperCase()}</span>
          {ft != null && <><span>·</span><span>{ft}FT</span></>}
          <span>·</span><span>{ago}</span>
          {remaining != null && (
            <>
              <span>·</span>
              <span
                className={expiringSoon ? "text-[#FF1A1A] font-bold" : "text-zinc-500"}
                data-testid={`vibe-ttl-${v.id}`}
              >EXP {fmtRemaining(remaining)}</span>
            </>
          )}
          {isBiz && (
            <span className="ml-auto px-2 py-0.5 rounded-full bg-[#E60000] text-white text-[9px] flex items-center gap-1">● HOT DEAL</span>
          )}
        </div>
        {v.text && <div className="mt-2 text-white font-bold text-lg leading-tight"><TranslatedText text={v.text} testId={`vibe-tx-${v.id}`} /></div>}
        {v.content_type === "gif" && v.gif_url && (
          <div className="mt-2">
            <GifImage src={v.gif_url} maxHeight={200} testId={`vibe-gif-${v.id}`} />
          </div>
        )}
        {v.venue_label && (
          <div className="mt-1 text-zinc-500 text-[11px] font-mono-accent tracking-wider">{v.venue_label.toUpperCase()}</div>
        )}
        <div className="mt-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-1.5">
              {[0,1,2].slice(0, Math.min(3, v.interest_count || 0)).map(i => (
                <div key={i} className="w-5 h-5 rounded-full bg-[#E60000] border-2 border-black shadow-[0_0_6px_rgba(230,0,0,0.6)]" />
              ))}
            </div>
            <span className="text-zinc-400 text-xs">{t("liveRoom.interested", { count: v.interest_count || 0 })}</span>
          </div>
          {!v.is_self && (
            <div className="flex items-center gap-2">
              <span
                data-testid={`vibe-comment-hint-${v.id}`}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-white/10 text-zinc-300 text-xs hover:border-[#E60000]/40 hover:text-white transition"
                aria-label="Send a private comment"
              >
                <MessageSquare size={12} className="text-[#E60000]" /> {t("liveRoom.comment")}
              </span>
              <button
                onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); blink(v); }}
                disabled={v.i_blinked}
                data-testid={`vibe-blink-${v.id}`}
                className={`px-5 py-2 rounded-full font-bold text-sm transition active:scale-[0.92] ${
                  v.i_blinked
                    ? "bg-[#E60000]/20 text-[#E60000] border border-[#E60000]/40"
                    : "bg-gradient-to-r from-[#E60000] to-[#660000] text-white shadow-[0_0_18px_rgba(230,0,0,0.5)] hover:brightness-110"
                }`}
              >
                {v.i_blinked ? t("liveRoom.blinked") : t("liveRoom.blink")}
              </button>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
